'use client';

import { useIsMobile, useIsTablet } from '@/components/hooks/use-mobile';
import { StaticImport } from 'next/dist/shared/lib/get-img-props';
import NextImage, { ImageLoaderProps, ImageProps as NextImageProps } from 'next/image';
import React from 'react';

type ResponsiveSize = {
  mobile?: number;
  tablet?: number;
  desktop: number;
};

type ImageProps = {
  src?: string | StaticImport | null;
  width?: number | ResponsiveSize;
  height?: number | ResponsiveSize;
} & Omit<NextImageProps, 'src' | 'width' | 'height'>;

const contentfulLoader = ({ src, width, quality }: ImageLoaderProps) => {
  return `${src}?w=${width}&q=${quality || 75}`;
};

const externalLoader = ({ src }: ImageLoaderProps) => {
  return src;
};

export const remoteHosts = ['images.ctfassets.net', 'images.eu.ctfassets.net'];

const resolveResponsiveSize = (
  size: number | ResponsiveSize | undefined,
  isMobile: boolean | undefined,
  isTablet: boolean | undefined,
): number | undefined => {
  if (size === undefined) {
    return undefined;
  }

  // If it's a number, use it directly
  if (typeof size === 'number') {
    return size;
  }

  // If it's a responsive size object, resolve based on breakpoints
  // Resolution order: mobile → tablet → desktop
  if (isMobile === true && size.mobile !== undefined) {
    return size.mobile;
  }

  if (isTablet === true && size.tablet !== undefined) {
    return size.tablet;
  }

  // Fall back to desktop (required)
  return size.desktop;
};

export const Image = ({ src, width, height, loader, unoptimized, ...props }: ImageProps) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  if (!src) {
    return null;
  }

  // Resolve responsive sizes
  const resolvedWidth = resolveResponsiveSize(width, isMobile, isTablet);
  const resolvedHeight = resolveResponsiveSize(height, isMobile, isTablet);

  if (typeof src === 'string' && src.startsWith('http')) {
    const url = new URL(src);

    if (!remoteHosts.includes(url.hostname)) {
      loader = externalLoader;
      unoptimized = true;
    }
  }

  if (typeof src === 'string' && src.startsWith('//')) {
    src = `https:${src}`;
    loader = contentfulLoader;
  }

  // Serve SVGs as-is to preserve vector quality
  const isSvg = typeof src === 'string' && src.includes('.svg');
  if (isSvg) {
    loader = externalLoader;
    unoptimized = true;
  }

  return (
    <NextImage
      src={src}
      width={resolvedWidth}
      height={resolvedHeight}
      loader={loader}
      unoptimized={unoptimized}
      {...props}
    />
  );
};
